import React, { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Context } from '../../app/Context';
import type { Sidebar as Interface } from '../../design/components';
import { iconMap } from './iconMap';
import { AdaIcon } from '../shared/AdaIcon';

export const Sidebar: React.FC<Interface> = ({ state, onAssistantToggle, assistantOpen }) => {
  const [context, setContext] = useContext(Context)!
  const models = [...new Map(context.authn
    .filter(auth => auth.project?.id === context.project?.id)
    .map(auth => auth.capability?.model)
    .filter(model => model?.id != null)
    .map(model => [model!.id, model!])).values()]
    .filter(model => !model.hidden && model.type !== 'personal')
    .sort((a, b) => a.path === '/' ? -1 : b.path === '/' ? 1 : 0);
  const [collapsed, setCollapsed] = state || [false, () => {}];

  // Derive unique projects from all authorizations
  const authorizedProjects = useMemo(() => {
    const projectMap = new Map();
    context.authn.forEach(a => {
      if (a.project?.id) projectMap.set(a.project.id, a.project);
    });
    return Array.from(projectMap.values());
  }, [context.authn]);

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const proj = authorizedProjects.find(p => p.id === e.target.value);
    if (proj) setContext(prev => ({ ...prev, project: proj }));
  };

  return (
    <nav style={{ padding: '1rem', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
      {/* Project switcher */}
      <div style={{ marginBottom: '0.5rem', borderBottom: '1px solid rgba(0,0,0,0.08)', paddingBottom: '0.5rem' }}>
        <select
          value={context.project?.id ?? ''}
          onChange={handleProjectChange}
          style={{
            width: collapsed ? 'auto' : '100%',
            padding: '0.5rem',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: '4px',
            background: 'white',
            cursor: 'pointer'
          }}
          title={collapsed ? context.project?.name : undefined}
        >
          {authorizedProjects.map((proj) => (
            <option key={proj.id} value={proj.id}>
              {collapsed ? '📁' : proj.name}
            </option>
          ))}
        </select>
      </div>
      <div style={{ display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end', marginBottom: '0.5rem' }}>
        <button
          onClick={() => setCollapsed(!collapsed)}
          style={{ background: 'none', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '4px', padding: '0.25rem 0.5rem', cursor: collapsed ? 'e-resize' : 'w-resize' }}
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
        >
          {collapsed ? '☰' : '⟨'}
        </button>
      </div>
      {models.map((model) => (
        <Link
          key={model.id}
          to={model.path}
          style={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: '0.5rem', padding: '0.5rem 0', textDecoration: 'none' }}
          title={collapsed ? model.name : undefined}
        >
          {model.icon && <span>{iconMap[model.icon] || model.icon}</span>}
          {!collapsed && model.name}
        </Link>
      ))}
      <div style={{ flexGrow: 1 }} />
      <hr style={{ border: 'none', borderTop: '1px solid rgba(0,0,0,0.08)', margin: '0.5rem 0' }} />
      <Link
        to="#"
        onClick={(e) => { e.preventDefault(); onAssistantToggle?.(); }}
        style={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: '0.5rem', padding: '0.5rem 0', textDecoration: 'none' }}
        title={collapsed ? 'Ada' : undefined}
      >
        <AdaIcon size={20} animated={!assistantOpen} />
        {!collapsed && 'Ada'}
      </Link>
    </nav>
  );
};
